import type { SupabaseClient } from "@supabase/supabase-js";

export type BillingProviderId = "henrri";
export type BillingDocumentKind = "quote" | "invoice";
export type BillingDocumentAction =
  | "preview"
  | "create_draft"
  | "finalize_quote"
  | "transform_to_invoice";
export type BillingQuoteLifecycleAction =
  | "preview"
  | "finalize_quote"
  | "transform_to_invoice";
export type BillingPrivateDocumentFormat = "pdf" | "facturx";

export type BillingProviderCapabilities = {
  documentPreview: boolean;
  sandboxDraftCreation: boolean;
  privatePdf: boolean;
  facturX: boolean;
  paymentRead: boolean;
  paymentWrite: boolean;
  webhooks: boolean;
  sandboxQuoteTransformation: boolean;
};

export type BillingDocumentPreparation = {
  idempotency_key: string;
  action: BillingDocumentAction;
  environment: "sandbox";
  document_type: BillingDocumentKind;
  document_id: string;
  document_number: string;
  client: {
    id: string;
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  lines: Array<{
    description: string;
    quantity: number;
    unit_price_ex_vat: number;
    vat_rate: number;
  }>;
  total_ex_vat: number;
  already_linked: boolean;
  external_effect: "none" | "sandbox_draft_created";
  external_document?: {
    id?: string | number;
    identity?: string | null;
    state?: string;
  } | null;
  reused?: boolean;
  message: string;
};

type ProviderDocumentPreparation = Omit<
  BillingDocumentPreparation,
  "idempotency_key"
>;

export type BillingPrivateDocument = {
  url: string;
  format: BillingPrivateDocumentFormat;
  cached: boolean;
  expires_in: number;
  sha256: string;
  size_bytes: number;
};

export type BillingPaymentSync = {
  environment: "sandbox";
  staged: number;
  matches: {
    exact: number;
    multiple: number;
    unmatched: number;
  };
  cursor: number;
  invoices_changed: false;
  message: string;
};

export type BillingQuoteLifecycle = {
  action: BillingQuoteLifecycleAction;
  environment: "sandbox";
  quote_id: string;
  quote_number: string;
  quote_status: string;
  source_external_id: string;
  source_identity: string | null;
  source_state: string;
  external_effect:
    | "none"
    | "sandbox_quote_finalized"
    | "sandbox_invoice_draft_created";
  transformation?: Record<string, unknown> | null;
  invoice_id?: string | null;
  target_external_id?: string | null;
  target_identity?: string | null;
  target_state?: string | null;
  reused?: boolean;
  message: string;
};

export type BillingProviderResult<T> =
  | { ok: true; data: T; error: null }
  | { ok: false; data: null; error: string };

export type BillingProvider = {
  id: BillingProviderId;
  label: string;
  capabilities: BillingProviderCapabilities;
  prepareDocument(input: {
    action: BillingDocumentAction;
    documentType: BillingDocumentKind;
    documentId: string;
    idempotencyKey: string;
  }): Promise<BillingProviderResult<BillingDocumentPreparation>>;
  getPrivateDocument(input: {
    documentType: "invoice";
    documentId: string;
    format: BillingPrivateDocumentFormat;
  }): Promise<BillingProviderResult<BillingPrivateDocument>>;
  syncPayments(): Promise<BillingProviderResult<BillingPaymentSync>>;
  manageQuote(input: {
    action: BillingQuoteLifecycleAction;
    quoteId: string;
    idempotencyKey: string;
  }): Promise<BillingProviderResult<BillingQuoteLifecycle>>;
};

type FunctionError = Error & { context?: Response };

async function normalizedFunctionError(error: unknown, fallback: string) {
  const candidate = error as FunctionError | null;
  let detail = candidate?.message || fallback;
  try {
    const payload = await candidate?.context?.clone().json();
    if (payload?.error) detail = String(payload.error);
  } catch {
    // Preserve the transport message when the response is not JSON.
  }
  return detail;
}

export function createBillingIdempotencyKey(input: {
  provider: BillingProviderId;
  action: BillingDocumentAction;
  documentType: BillingDocumentKind;
  documentId: string;
}) {
  return [
    "billing",
    input.provider,
    input.action,
    input.documentType,
    input.documentId.trim(),
  ]
    .map((part) => encodeURIComponent(part))
    .join(":");
}

export function createHenrriBillingProvider(
  client: SupabaseClient,
  options: {
    privateDocumentsEnabled: boolean;
    paymentReadEnabled?: boolean;
    quoteTransformationEnabled?: boolean;
  },
): BillingProvider {
  const capabilities: BillingProviderCapabilities = {
    documentPreview: true,
    sandboxDraftCreation: true,
    privatePdf: options.privateDocumentsEnabled,
    facturX: options.privateDocumentsEnabled,
    paymentRead: options.paymentReadEnabled === true,
    paymentWrite: false,
    webhooks: false,
    sandboxQuoteTransformation: options.quoteTransformationEnabled === true,
  };
  return {
    id: "henrri",
    label: "Henrri",
    capabilities,
    async prepareDocument(input) {
      if (!input.idempotencyKey.trim())
        return {
          ok: false,
          data: null,
          error: "La clé anti-doublon de l’opération est absente.",
        };
      const result = await client.functions.invoke<ProviderDocumentPreparation>(
        "henrri-export-document",
        {
          body: {
            action: input.action,
            document_type: input.documentType,
            document_id: input.documentId,
            idempotency_key: input.idempotencyKey,
            ...(input.action === "create_draft"
              ? { confirmation: "CREER BROUILLON HENRRI SANDBOX" }
              : {}),
          },
        },
      );
      if (result.error)
        return {
          ok: false,
          data: null,
          error: await normalizedFunctionError(
            result.error,
            "Le fournisseur de facturation a refusé l’opération.",
          ),
        };
      if (!result.data)
        return {
          ok: false,
          data: null,
          error: "Le fournisseur de facturation n’a retourné aucun contrôle exploitable.",
        };
      return {
        ok: true,
        data: { ...result.data, idempotency_key: input.idempotencyKey },
        error: null,
      };
    },
    async getPrivateDocument(input) {
      if (
        (input.format === "pdf" && !capabilities.privatePdf) ||
        (input.format === "facturx" && !capabilities.facturX)
      )
        return {
          ok: false,
          data: null,
          error: "Le module documentaire privé n’est pas activé sur cette base.",
        };
      const result = await client.functions.invoke<BillingPrivateDocument>(
        "henrri-document-pdf",
        {
          body: {
            document_type: input.documentType,
            document_id: input.documentId,
            format: input.format,
          },
        },
      );
      if (result.error)
        return {
          ok: false,
          data: null,
          error: await normalizedFunctionError(
            result.error,
            "Le document du fournisseur n’a pas pu être ouvert.",
          ),
        };
      if (!result.data?.url)
        return {
          ok: false,
          data: null,
          error: "Le fournisseur n’a retourné aucun lien de document exploitable.",
        };
      return { ok: true, data: result.data, error: null };
    },
    async syncPayments() {
      if (!capabilities.paymentRead)
        return {
          ok: false,
          data: null,
          error: "La lecture des règlements n’est pas activée sur cette base.",
        };
      const result = await client.functions.invoke<BillingPaymentSync>(
        "henrri-payment-sync",
        { body: { environment: "sandbox" } },
      );
      if (result.error)
        return {
          ok: false,
          data: null,
          error: await normalizedFunctionError(
            result.error,
            "La lecture des règlements du fournisseur a échoué.",
          ),
        };
      if (!result.data || result.data.invoices_changed !== false)
        return {
          ok: false,
          data: null,
          error:
            "Le fournisseur n’a pas confirmé un rapprochement sans modification automatique.",
        };
      return { ok: true, data: result.data, error: null };
    },
    async manageQuote(input) {
      if (!capabilities.sandboxQuoteTransformation)
        return {
          ok: false,
          data: null,
          error: "La transformation devis → facture n’est pas encore déployée sur cette base.",
        };
      if (!input.idempotencyKey.trim())
        return {
          ok: false,
          data: null,
          error: "La clé anti-doublon de l’opération est absente.",
        };
      const result = await client.functions.invoke<BillingQuoteLifecycle>(
        "henrri-transform-quote",
        {
          body: {
            action: input.action,
            quote_id: input.quoteId,
            idempotency_key: input.idempotencyKey,
            ...(input.action === "finalize_quote"
              ? { confirmation: "FINALISER DEVIS HENRRI SANDBOX" }
              : input.action === "transform_to_invoice"
                ? { confirmation: "TRANSFORMER DEVIS HENRRI SANDBOX" }
                : {}),
          },
        },
      );
      if (result.error)
        return {
          ok: false,
          data: null,
          error: await normalizedFunctionError(
            result.error,
            "Le cycle devis → facture a été refusé par le fournisseur.",
          ),
        };
      if (!result.data)
        return {
          ok: false,
          data: null,
          error: "Le fournisseur n’a retourné aucun état exploitable.",
        };
      return { ok: true, data: result.data, error: null };
    },
  };
}
